import { NgClass } from '@angular/common'
import { Component, DestroyRef, inject, Input } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup } from '@angular/forms'
import { ToastrService } from '@app/services/toastr.service'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { BADGE_SIDES } from '../../../enums/badge.enum'
import { BadgeEditorService, updateBadgePayload } from '../../../services/badge-editor.service'
@Component({
  selector: 'app-badge-tooltip-settings',
  templateUrl: './badge-tooltip-settings.component.html',
  styleUrls: ['./badge-tooltip-settings.component.scss'],
  standalone: true,
  imports: [NgClass, NgxTippyModule]
})
export class BadgeTooltipSettingsComponent {
  @Input() printBackgroundControl!: FormControl
  @Input() background?: string
  @Input() badgeFormGroup!: FormGroup
  @Input() currentSide!: BADGE_SIDES
  @Input() aspectRatio!: number
  public togglePreprintBackground: boolean = false

  /**
   * Cloudinary upload widget reference for badge background
   */
  cloudinaryUploadWidgetForBadgeBackground: any
  private readonly _destroyRef = inject(DestroyRef)

  constructor(private _BadgeService: BadgeEditorService, private _ToastrService: ToastrService) {}

  public get enablePreprintTooltipText(): string {
    return this.printBackgroundControl.value && !this.togglePreprintBackground ? 'Your badge is preprinted' : 'Enable preprinting fixed elements onsite'
  }

  public get disablePreprintTooltipText(): string {
    return !this.togglePreprintBackground && !this.printBackgroundControl.value ? 'Your badge is not preprinted' : 'Disable preprinting fixed elements onsite'
  }

  /**
   * Opens the upload modal for badge background with aspect ratio
   * @param aspectRatio Optional aspect ratio (default: 1.0 for square)
   * @returns void
   */
  public toggleUploadBadgeBackgroundModal(): void {
    this.aspectRatio, 'aspectRatio???'
    if (!this.cloudinaryUploadWidgetForBadgeBackground) {
      this.bootBadgeBackgroundUpload()
    }
    if (!this.cloudinaryUploadWidgetForBadgeBackground?.isShowing()) {
      this.cloudinaryUploadWidgetForBadgeBackground.open()
    }
  }

  /**
   * Bootstraps a cloudinary widget for badge background upload
   * @returns void
   */
  private bootBadgeBackgroundUpload(): void {
    this.cloudinaryUploadWidgetForBadgeBackground = (window as any).cloudinary.createUploadWidget(
      {
        cloud_name: 'contactless',
        upload_preset: 'wydqifyd',
        cropping: true,
        croppingAspectRatio: this.aspectRatio,
        croppingShowDimensions: true,
        maxFileSize: 5000000,
        minImageWidth: 200,
        croppingValidateDimensions: true,
        showSkipCropButton: false,
        clientAllowedFormats: ['jpeg', 'jpg', 'png']
      },
      (error: any, result: any) => {
        if (!error && result && result.event === 'success') {
          this.handleBadgeBackgroundUpload(result.info.secure_url)
        }
        if (result?.failed && !result?.event) {
          setTimeout(() => {
            this.cloudinaryUploadWidgetForBadgeBackground.destroy()
            this.bootBadgeBackgroundUpload()
            this.cloudinaryUploadWidgetForBadgeBackground.open()
          }, 2500)
        }
      }
    )
  }

  /**
   * Handles the uploaded badge background
   * @param imageUrl The uploaded image URL
   * @returns void
   */
  private handleBadgeBackgroundUpload(imageUrl: string): void {
    let payload: updateBadgePayload = {
      badge_front_html_content: this.badgeFormGroup.value.badge_front_html_content,
      background_preprinted: this.badgeFormGroup.value.background_preprinted,
      form_id: this.badgeFormGroup.value.id,
      reset: false,
      front_fixed_element: imageUrl
    }
    this._BadgeService.frontFixedElement = imageUrl

    this._BadgeService
      .saveBadge(payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._ToastrService.success('Success', 'Badge background has been uploaded successfully')
          this._BadgeService.badgeBackgroundUploadChanges$.next({ front_fixed_element: imageUrl })
        },
        error: (error) => {
          this._ToastrService.error('Error', 'Failed to upload badge background')
          console.error('Upload error:', error)
        }
      })
  }

  /**
   * @method togglePrintingUploadedBackground
   * @description toggle Printing Uploaded Background flag to save it later with API
   * @param value accepts boolean
   */
  public togglePrintingUploadedBackground(value: boolean): void {
    this.togglePreprintBackground = !this.togglePreprintBackground
    if (value === this.printBackgroundControl.value || this.togglePreprintBackground) return
    let payload: updateBadgePayload = {
      badge_front_html_content: this.badgeFormGroup.value.badge_front_html_content,
      background_preprinted: value,
      form_id: this.badgeFormGroup.value.id,
      front_fixed_element: this._BadgeService.frontFixedElement,
      reset: false
    }
    this._BadgeService
      .saveBadge(payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._ToastrService.success('Success', 'Preprinting fixed elements has been updated')
          this.printBackgroundControl.setValue(value)
        }
      })
  }

  /**
   * @method deleteUploadedBackground
   * @description delete uploaded background
   * @returns void
   */
  public deleteUploadedBackground(): void {
    let payload: updateBadgePayload = {
      badge_front_html_content: this.badgeFormGroup.value.badge_front_html_content,
      background_preprinted: this.badgeFormGroup.value.background_preprinted,
      form_id: this.badgeFormGroup.value.id,
      front_fixed_element: null,
      reset: false
    }
    this._BadgeService.frontFixedElement = null

    this._BadgeService
      .saveBadge(payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._ToastrService.success('Success', 'Fixed elements image has been deleted successfully')
          this._BadgeService.badgeBackgroundUploadChanges$.next(null)
        }
      })
  }
}
